/**
 * Iter60.p43.3 — "Early Departures This Week" Home dashboard tile.
 *
 * Compact tile summarising early-departure activity across the trailing
 * 7-day window (reuses the same window as the other Home KPIs, driven
 * by `GET /api/dashboard/early-departures/week`).
 *
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │  EARLY DEPARTURES • This Week          [ View details → ]     │
 *   │  15                       Excused 6 · Unexcused 9              │
 *   │  Medical 4 · Family 5 · School 2 · Other 4                     │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * Read-only for every role — the modal owns the drill-down + CSV export.
 */
import React from "react";
import { LogOut } from "lucide-react";

const REASON_LABELS = {
    medical: "Medical",
    family: "Family",
    school: "School",
    other: "Other",
};

export default function EarlyDeparturesWeekTile({
    data,
    loading,
    onOpen,
    style,
}) {
    const total = data?.total ?? 0;
    const byReason = data?.by_reason || {};
    const excused = data?.excused ?? 0;
    const unexcused = data?.unexcused ?? 0;
    const rangeLabel = data?.range?.label || "";

    return (
        <button
            type="button"
            onClick={onOpen}
            disabled={loading}
            className="asl-bento-tile asl-early-dep-tile"
            data-testid="home-tile-early-departures"
            style={{ gridColumn: "1 / span 12", gridRow: "6", ...style }}
        >
            <div className="asl-early-dep-tile__head">
                <div>
                    <div
                        className="text-label"
                        style={{ color: "var(--asl-crimson-2)" }}
                        data-testid="early-dep-tile-label"
                    >
                        EARLY DEPARTURES · THIS WEEK
                    </div>
                    <div
                        className="text-xs mt-0.5"
                        style={{ color: "var(--asl-text-3)" }}
                        data-testid="early-dep-tile-range"
                    >
                        {loading ? "…" : rangeLabel || "Trailing 7 days"}
                    </div>
                </div>
                <div
                    className="asl-early-dep-tile__cta"
                    data-testid="early-dep-tile-view-details"
                >
                    <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                    View details
                </div>
            </div>

            {loading ? (
                <div
                    className="asl-shimmer w-full rounded"
                    style={{ height: 88, marginTop: 12 }}
                    data-testid="early-dep-tile-loading"
                />
            ) : (
                <div className="asl-early-dep-tile__body">
                    <div className="asl-early-dep-tile__total">
                        <div
                            className="mono"
                            style={{
                                fontSize: 36,
                                fontWeight: 700,
                                color: "var(--asl-navy-2, #1B2A56)",
                                lineHeight: 1,
                            }}
                            data-testid="early-dep-tile-total"
                        >
                            {total}
                        </div>
                        <div
                            className="text-xs"
                            style={{ color: "var(--asl-text-3)", marginTop: 4 }}
                        >
                            {total === 1 ? "note recorded" : "notes recorded"}
                        </div>
                    </div>

                    <div className="asl-early-dep-tile__stats">
                        <div className="asl-early-dep-tile__pills">
                            <Pill
                                testid="early-dep-tile-excused"
                                label="Excused"
                                value={excused}
                                tone="ok"
                            />
                            <Pill
                                testid="early-dep-tile-unexcused"
                                label="Unexcused"
                                value={unexcused}
                                tone={unexcused > 0 ? "danger" : "muted"}
                            />
                        </div>
                        <div
                            className="asl-early-dep-tile__reasons"
                            data-testid="early-dep-tile-reasons"
                        >
                            {Object.keys(REASON_LABELS).map((code) => (
                                <ReasonChip
                                    key={code}
                                    code={code}
                                    label={REASON_LABELS[code]}
                                    value={byReason[code] ?? 0}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </button>
    );
}

function Pill({ label, value, tone, testid }) {
    const bg =
        tone === "danger"
            ? "rgba(168, 50, 74, 0.10)"
            : tone === "ok"
                ? "rgba(27, 42, 86, 0.08)"
                : "rgba(0, 0, 0, 0.04)";
    const color =
        tone === "danger"
            ? "var(--asl-crimson, #A8324A)"
            : tone === "ok"
                ? "var(--asl-navy-2, #1B2A56)"
                : "var(--asl-text-2)";
    return (
        <span
            className="asl-early-dep-tile__pill"
            style={{ background: bg, color }}
            data-testid={testid}
        >
            <span className="asl-early-dep-tile__pill-label">{label}</span>
            <span className="asl-early-dep-tile__pill-value mono">{value}</span>
        </span>
    );
}

function ReasonChip({ code, label, value }) {
    return (
        <span
            className="asl-early-dep-tile__reason"
            data-testid={`early-dep-tile-reason-${code}`}
        >
            <span className="asl-early-dep-tile__reason-label">{label}</span>
            <span className="asl-early-dep-tile__reason-value mono">
                {value}
            </span>
        </span>
    );
}
